import { useInfiniteCarousel } from "@hooks/useInfiniteCarousel";
import type {
  Media,
  QuoteCarouselComponentProps,
} from "@services/wordpress/wordpress.types";
import Icons from "@components/icons.ts";
import { Slide } from "@components/Sliders/Slide";
import { Slider } from "@components/Sliders/Slider";

interface Props extends Omit<QuoteCarouselComponentProps, "media"> {
  media?: Media;
}

export const CustomCarousel = ({
  acf_fc_layout,
  slides,
  id,
  media,
}: Props & { id: string }) => {
  const {
    activeSlide,
    handleNavigate,
    disabled,
    slides: readySlides,
    ...scrollHook
  } = useInfiniteCarousel(slides, id);

  return (
    <div className="QuoteCarouselDisplay flex flex-col lg:flex-row border-t-gt-black border-b-gt-black border-b-solid border-t-solid border-b border-t w-full">
      <div id={id} class="QuoteCarouselDisplay__container bg-gt-lilac lg:w-2/4 order-2 lg:order-1">
        <Slider
          {...scrollHook}
          controlsNode={
            <div class="Slider__controls absolute bottom-8 lg:bottom-16 right-8 lg:right-16 z-20 flex justify-end items-center gap-8">
              <button
                disabled={disabled}
                class="Slider__controls-button m-0 flex cursor-pointer items-center justify-center border border-solid border-gt-black p-2 md:p-4 text-3xl rounded bg-gt-lilac"
                onClick={handleNavigate(activeSlide - 1)}
                aria-label="Previous slide"
              >
                <Icons.IconLeft />
              </button>
              <button
                disabled={disabled}
                class="Slider__controls-button m-0 flex cursor-pointer items-center justify-center p-2 md:p-4 border border-solid border-gt-black text-3xl rounded bg-gt-lilac"
                onClick={handleNavigate(activeSlide + 1)}
                aria-label="Next slide"
              >
                <Icons.IconRight />
              </button>
            </div>
          }
          heading=""
          subheading=""
        >
          {readySlides.map((slide, index) => (
            <Slide
              active={index === activeSlide}
              key={index}
              carouselType="quote"
            >
              <blockquote class="QuoteCarouselDisplay__slide-quote w-full mx-0">
                <div class="w-full flex flex-col">
                  <p className="text-[3.5rem] md:text-[4rem]">“</p>
                  <p class="QuoteCarouselDisplay__slide-quote-text text-2xl md:text-[2rem]">{slide.quote}</p>
                </div>
                <div className="w-full flex flex-col mt-4 mb-6">
                  <cite className="text-[1.125rem] font-bold not-italic">
                    {slide.attributed}
                  </cite>
                  <p>{slide.attributedTitle}</p>
                </div>
              </blockquote>
            </Slide>
          ))}
        </Slider>
      </div>
      <div
        className="QuoteCarouselDisplay__media basis-1/2 order-1 lg:order-2 aspect-square lg:border-l lg:border-l-solid lg:border-l-gt-black">
        {media?.media_type === "image" && (
          <img
            className="object-cover w-full h-full"
            src={media.source_url}
            alt={media.alt_text}
          />
        )}
        {media?.mime_type.includes("video") && (
          <video
            className="object-cover w-full h-full"
            src={`${media.source_url}#t=2}`}
            alt={media.alt_text}
            playsInline
            autoPlay
            loop
            muted
          />
        )}
      </div>
    </div>
  );
};
